import { useState, type Dispatch, type SetStateAction } from "react";

interface SelectableItem {
  id: number;
}

interface UseRangeAndToggleSelectionOptions {
  /** Setter for the list of currently selected item ids. */
  setSelectedIds: Dispatch<SetStateAction<number[]>>;
  /**
   * Id of the item that is "focused" outside of the bulk selection (e.g. the last plain-clicked
   * row). Used as the range-selection anchor and as the toggle-selection baseline before the user
   * has made any Cmd/Ctrl selection yet.
   */
  focusedId: number | null | undefined;
  /** Optional callback invoked with the new selection whenever it changes (e.g. a checked-count). */
  onSelectionChange?: (selectedIds: number[]) => void;
}

/**
 * Shared shift-click range-selection and Cmd/Ctrl toggle-selection logic used by the project and
 * file lists in the Project Explorer. Keeping this in one place avoids the two panels drifting
 * apart the way other duplicated UI has in this repo (see CLAUDE.md's note on leftover copies).
 */
export function useRangeAndToggleSelection<T extends SelectableItem>({
  setSelectedIds,
  focusedId,
  onSelectionChange,
}: UseRangeAndToggleSelectionOptions) {
  // Shift-click uses this anchor to select a contiguous range of items in the currently visible list order.
  const [selectionAnchorId, setSelectionAnchorId] = useState<number | null>(null);

  const applyRangeSelection = (visibleItems: T[], targetItem: T) => {
    const anchorId = selectionAnchorId ?? focusedId ?? null;

    if (anchorId === null) {
      setSelectedIds([targetItem.id]);
      onSelectionChange?.([targetItem.id]);
      return;
    }

    const anchorIndex = visibleItems.findIndex((item) => item.id === anchorId);
    const targetIndex = visibleItems.findIndex((item) => item.id === targetItem.id);

    if (anchorIndex === -1 || targetIndex === -1) {
      setSelectedIds([targetItem.id]);
      onSelectionChange?.([targetItem.id]);
      return;
    }

    const startIndex = Math.min(anchorIndex, targetIndex);
    const endIndex = Math.max(anchorIndex, targetIndex);
    const nextSelectedIds = visibleItems.slice(startIndex, endIndex + 1).map((item) => item.id);

    setSelectedIds(nextSelectedIds);
    onSelectionChange?.(nextSelectedIds);
  };

  const toggleSelection = (itemId: number) => {
    setSelectedIds((previousSelectedIds) => {
      // If the user previously plain-clicked an item, treat that focused item as the starting point
      // for the first Cmd/Ctrl additive selection even though it is not yet in the selection.
      const baselineSelectedIds =
        previousSelectedIds.length === 0 && focusedId != null && focusedId !== itemId
          ? [focusedId]
          : previousSelectedIds;

      const nextSelectedIds = baselineSelectedIds.includes(itemId)
        ? baselineSelectedIds.filter((selectedId) => selectedId !== itemId)
        : [...baselineSelectedIds, itemId];

      onSelectionChange?.(nextSelectedIds);
      return nextSelectedIds;
    });
  };

  return { selectionAnchorId, setSelectionAnchorId, applyRangeSelection, toggleSelection };
}
